// The consent page for "Connect your AI" (/connect, from connector.js's /oauth/authorize): the
// trader signs in if needed, sees which app wants to read their journal and where it sends them
// back, and says Allow or Cancel. Read-only access.
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { api } from './api';
import { Logo } from './Brand';
import { useI18n } from './i18n';

export const AFTER_LOGIN_KEY = 'mm-after-login';

export default function ConnectPage({ user }) {
  const { t } = useI18n();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(params.get('error') || '');

  useEffect(() => {
    if (!user || user.isDemo || params.get('error')) return;
    api(`/connector/request${location.search}`).then(setInfo).catch((err) => setError(err.message));
  }, [user, location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  function signIn() {
    try { sessionStorage.setItem(AFTER_LOGIN_KEY, `${location.pathname}${location.search}`); } catch { /* sign in, then open the link again */ }
  }

  async function answer(approve) {
    setBusy(true);
    setError('');
    try {
      const r = await api('/connector/approve', { method: 'POST', body: { ...Object.fromEntries(params.entries()), approve } });
      window.location.assign(r.redirect);
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  }

  const items = ['Your trades, accounts and prop firm rules', 'Your results and statistics', 'Your journal notes, plans and reviews', 'Your Playbook and trading rules'];
  return (
    <main className="connect-page">
      <div className="connect-card">
        <div className="connect-brand"><Logo /> <strong>Market Matrix</strong></div>
        {error ? (
          <>
            <h1>{t('This connection can’t continue')}</h1>
            <p className="error">{t(error)}</p>
            <Link to="/app/home" className="secondary small">{t('Go to Market Matrix')}</Link>
          </>
        ) : !user || user.isDemo ? (
          <>
            <h1>{t('Connect your AI to Market Matrix')}</h1>
            <p>{t('Log in to your Market Matrix account to continue.')}</p>
            <Link to="/login" className="connect-cta" onClick={signIn}>{t('Log in')}</Link>
          </>
        ) : !info ? <p className="empty">{t('Loading…')}</p> : !info.available ? (
          <>
            <h1>{t('Not available yet')}</h1>
            <p>{t('Connecting an AI isn’t available on your account yet.')}</p>
            <button type="button" className="secondary" onClick={() => answer(false)} disabled={busy}>{t('Go back')}</button>
          </>
        ) : !info.ok ? (
          <>
            <h1>{t('This connection can’t continue')}</h1>
            <p className="error">{t(info.error || 'Something went wrong. Please try again.')}</p>
          </>
        ) : (
          <>
            <h1>{t('{app} wants to read your journal', { app: info.app })}</h1>
            <p>{t('Signed in as {name}. If you allow it, {app} can read:', { name: user.name, app: info.app })}</p>
            <ul className="connect-list">{items.map((x) => <li key={x}>✓ {t(x)}</li>)}</ul>
            <p className="connect-note">🔒 {t('Read-only: it can’t add, change or delete anything. You can disconnect it any time in Settings.')}</p>
            <p className="field-hint">{t('You’ll be sent back to {host}.', { host: info.redirectHost })}</p>
            <div className="connect-actions">
              <button type="button" className="secondary" onClick={() => answer(false)} disabled={busy}>{t('Cancel')}</button>
              <button type="button" onClick={() => answer(true)} disabled={busy}>{busy ? t('Connecting…') : t('Allow')}</button>
            </div>
          </>
        )}
      </div>
    </main>
  );
}
